/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { useMartItemMenuDisplayV2Query } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { mapItemsV2ToItems } from "utils/menuV3Mapper"

interface UseItemListItemsReturn {
  items: Item[]
  isLoading: boolean
}

/**
 * Hook to get all items for ItemList component
 * When enablePartialMenu is true, fetches from query and flattens all items
 * Otherwise, uses Redux items
 */
export const useItemListItems = (): UseItemListItemsReturn => {
  const enablePartialMenu = useEnablePartialMenu()

  // Get data from Redux
  const {
    merchantID,
    merchantGroupID,
    isFetchingMenu,
    items: reduxItems,
  } = useSelector((state: AppState) => state?.menu)

  // Use martItemMenuDisplayV2 query when enablePartialMenu is true
  const {
    data: martMenuData,
    isLoading: isQueryLoading,
  } = useMartItemMenuDisplayV2Query(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID,
    },
  )

  // Get all items from query or Redux
  const items = useMemo(() => {
    if (enablePartialMenu && martMenuData) {
      // Flatten all items from all departments and sub-departments
      const allItems: Item[] = []
      martMenuData.departments?.forEach(department => {
        department.subDepartments?.forEach(subDepartment => {
          if (subDepartment.items) {
            const mappedItems = mapItemsV2ToItems(subDepartment.items)
            // Add hierarchy context to items
            const itemsWithContext = mappedItems.map(item => ({
              ...item,
              departmentID: department.itemClassID,
              departmentName: department.itemClassName,
              subDepartmentID: subDepartment.itemClassID,
              subDepartmentName: subDepartment.itemClassName,
            }))
            allItems.push(...itemsWithContext)
          }
        })
      })
      return allItems
    }
    return reduxItems
  }, [enablePartialMenu, martMenuData, reduxItems])

  // Determine loading state: use query loading when partial menu is enabled, otherwise use Redux loading
  const isLoading = enablePartialMenu ? isQueryLoading : isFetchingMenu

  return {
    items,
    isLoading: !!isLoading,
  }
}
